import { RadioGroup as HeadlessRadioGroup } from '@headlessui/react';

interface RadioGroupOption<T = string> {
    value: T;
    label: string;
    description?: string;
}

interface RadioGroupProps<T = string> {
    label?: string;
    value: T;
    onChange: (value: T) => void;
    options: RadioGroupOption<T>[];
    error?: string;
    helperText?: string;
    /** Display options side by side on desktop (stacks on mobile) */
    inline?: boolean;
}

export function RadioGroup<T = string>({
    label,
    value,
    onChange,
    options,
    error,
    helperText,
    inline = false,
}: RadioGroupProps<T>) {
    return (
        <div className="w-full">
            <HeadlessRadioGroup value={value} onChange={onChange}>
                {label && (
                    <HeadlessRadioGroup.Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {label}
                    </HeadlessRadioGroup.Label>
                )}
                <div className={inline ? 'grid grid-cols-1 sm:grid-cols-2 gap-2 items-stretch' : 'space-y-2'}>
                    {options.map((option) => (
                        <HeadlessRadioGroup.Option
                            key={String(option.value)}
                            value={option.value}
                            className={({ checked }) =>
                                `relative flex cursor-pointer rounded-lg px-4 py-3 border ${
                                    checked
                                        ? 'border-accent bg-accent/10 dark:bg-accent/20'
                                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                                } focus:outline-hidden focus:ring-2 focus:ring-accent focus:ring-offset-2 dark:focus:ring-offset-dark`
                            }
                        >
                            {({ checked }) => (
                                <div className="flex w-full items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="text-sm">
                                            <HeadlessRadioGroup.Label
                                                as="p"
                                                className={`font-medium ${
                                                    checked
                                                        ? 'text-gray-900 dark:text-white'
                                                        : 'text-gray-900 dark:text-gray-100'
                                                }`}
                                            >
                                                {option.label}
                                            </HeadlessRadioGroup.Label>
                                            {option.description && (
                                                <HeadlessRadioGroup.Description
                                                    as="span"
                                                    className={`inline ${
                                                        checked
                                                            ? 'text-gray-600 dark:text-gray-300'
                                                            : 'text-gray-500 dark:text-gray-400'
                                                    }`}
                                                >
                                                    {option.description}
                                                </HeadlessRadioGroup.Description>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`shrink-0 text-accent ${checked ? 'opacity-100' : 'opacity-0'}`}>
                                        <svg
                                            className="h-5 w-5"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <circle cx="10" cy="10" r="4" />
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </HeadlessRadioGroup.Option>
                    ))}
                </div>
            </HeadlessRadioGroup>
            {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
            {helperText && !error && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
            )}
        </div>
    );
}
